import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DataEndChannel } from 'src/app/project/shared/interfaces/data-end-channel.interface';
import { DataEndChannelTypeEnum, getDataEndChannelTypeString } from 'src/app/project/shared/enums/data-end-channel-type.enum';
import { DataEndChannelService } from 'src/app/project/shared/services/data-end-channel.service';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { DataEndChannelSettingsDialogComponent } from 'src/app/project/project-settings/shared/data-end-channel-settings-dialog/data-end-channel-settings-dialog.component';
import { DigitalInputEngineParameterSettingsDialogComponent } from 'src/app/project/project-settings/shared/digital-input-engine-parameter-settings-dialog/digital-input-engine-parameter-settings-dialog.component';

/**
 * Öffnet den zum Komponententyp passenden Engine-Parameter-Dialog und speichert das Ergebnis.
 * Kapselt die zuvor nur in DataEndChannelTableComponent vorhandene Logik, damit sie auch
 * aus der SWA-Visualisierung wiederverwendet werden kann (gleicher Speicherweg: PUT DataEndChannel/{id}).
 */
@Injectable({ providedIn: 'root' })
export class EngineParameterEditService {
  constructor(
    private readonly dialog: MatDialog,
    private readonly dataEndChannelService: DataEndChannelService,
    private readonly alertService: AlertService
  ) { }

  public isDigitalInput(channel: DataEndChannel): boolean {
    return channel.dataEndChannelType === DataEndChannelTypeEnum.DIGITALINPUT;
  }

  /** true, wenn für den Kanal editierbare Engine-Parameter existieren. */
  public hasEngineParameters(channel: DataEndChannel): boolean {
    if (this.isDigitalInput(channel)) { return true; }
    return channel.fG01_Settings.length > 2
      || channel.fG04_Settings.length > 2
      || channel.fG06_Settings.length > 2;
  }

  private deriveFg(channel: DataEndChannel): number {
    if (channel.fG01_Settings.length > 2) { return 1; }
    if (channel.fG04_Settings.length > 2) { return 4; }
    if (channel.fG06_Settings.length > 2) { return 6; }
    return 0;
  }

  /**
   * Öffnet den Editor, speichert bei Bestätigung und liefert den aktualisierten Kanal,
   * bei Abbruch null.
   */
  public openEditor(
    channel: DataEndChannel,
    routeStationGuid: string,
    projectId: number | undefined,
    readonly = false
  ): Observable<DataEndChannel | null> {
    const digitalInput = this.isDigitalInput(channel);
    const fg = digitalInput ? 6 : this.deriveFg(channel);
    const settings = fg === 1 ? channel.fG01_Settings
      : fg === 4 ? channel.fG04_Settings
        : channel.fG06_Settings;
    const type = getDataEndChannelTypeString(channel.dataEndChannelType);
    const dialogData = { data: settings, fg, guid: routeStationGuid, type, projectId, readonly };

    const dialogRef = digitalInput
      ? this.dialog.open(DigitalInputEngineParameterSettingsDialogComponent, { data: dialogData })
      : this.dialog.open(DataEndChannelSettingsDialogComponent, { data: dialogData });

    return dialogRef.afterClosed().pipe(
      switchMap(result => {
        if (!result) { return of(null); }
        const engineParameter = '{"EngineParameter": ' + JSON.stringify(result.settings) + '}';
        if (result.fg === 1) { channel.fG01_Settings = engineParameter; }
        else if (result.fg === 4) { channel.fG04_Settings = engineParameter; }
        else if (result.fg === 6) { channel.fG06_Settings = engineParameter; }
        return this.dataEndChannelService.editDataEndChannel(channel.id, channel).pipe(
          switchMap(updated => {
            this.alertService.alert('Einstellungen wurden erfolgreich gespeichert', AlertTypes.info);
            return of(updated ?? channel);
          })
        );
      })
    );
  }
}
